import { ApiError } from '../types';

const API_BASE_URL = '/api';

/** Default request timeout in milliseconds (30 seconds). */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Reason passed to AbortController when abort is due to our timeout (not caller). */
const ABORT_REASON_TIMEOUT = Symbol('timeout');

/** Max retries for transient failures (5xx or network errors). */
const MAX_RETRIES = 2;

/** Delay before first retry in ms; doubles each retry. */
const RETRY_BASE_DELAY_MS = 500;

/** Provider for access tokens. Set by auth layer so requests include Bearer token. */
let tokenProvider: (() => Promise<string | null>) | null = null;

/** Callback for handling 401 unauthorized responses. Set by auth layer. */
let onUnauthorized: (() => void) | null = null;

/**
 * Register the function used to obtain access tokens for API requests.
 * Call with null to clear (e.g. on sign-out). Must be called from within auth context.
 */
export function setTokenProvider(provider: (() => Promise<string | null>) | null): void {
  tokenProvider = provider;
}

/**
 * Register a callback to handle 401 unauthorized responses.
 * The callback should trigger re-authentication (e.g., redirect to sign-in).
 * Call with null to clear (e.g. on sign-out).
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * Custom error class for API errors
 */
export class ApiRequestError extends Error {
  /** If true, retries were exhausted in a child call; do not retry again in catch. */
  readonly exhaustedRetries?: boolean;

  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public details?: Record<string, string>,
    exhaustedRetries?: boolean
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.exhaustedRetries = exhaustedRetries;
  }
}

/**
 * Whether an error is retryable (5xx or network failure). 401 is not retried.
 */
function isRetryableError(err: unknown, statusCode?: number): boolean {
  if (statusCode !== undefined && statusCode === 401) return false;
  if (err instanceof ApiRequestError) {
    return statusCode !== undefined && statusCode >= 500;
  }
  return err instanceof TypeError; // e.g. network failure
}

/**
 * Sleep for a given number of milliseconds.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generic fetch wrapper with error handling, timeout, and optional retry.
 * Always sends Authorization: Bearer &lt;token&gt; on every request (token from provider, or empty if none).
 * 401 responses indicate token expired or invalid; callers may trigger sign-in again.
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  let bearerToken: string | null = null;
  if (tokenProvider) {
    bearerToken = await tokenProvider();
    if (!bearerToken && import.meta.env.DEV) {
      console.warn(
        '[API] Request sent without access token (Authorization: Bearer will be empty). ' +
          'Check that VITE_ENTRA_API_SCOPE is set and that sign-in included the API scope; if silent token acquisition failed, a popup may be required.'
      );
    }
  }

  const controller = new AbortController();
  let callerAborted = false;
  const timeoutId = setTimeout(() => controller.abort(ABORT_REASON_TIMEOUT), DEFAULT_TIMEOUT_MS);
  if (options.signal) {
    options.signal.addEventListener('abort', () => {
      callerAborted = true;
      controller.abort(options.signal!.reason);
    });
  }

  const requestHeaders: Record<string, string> = {
    ...(defaultHeaders as Record<string, string>),
    ...(typeof options.headers === 'object' && !(options.headers instanceof Headers)
      ? (options.headers as Record<string, string>)
      : {}),
  };
  // Always send Authorization header on every backend call (Bearer token or empty)
  requestHeaders['Authorization'] = `Bearer ${bearerToken ?? ''}`;

  let lastStatus: number | undefined;
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: requestHeaders,
    });
    clearTimeout(timeoutId);
    lastStatus = response.status;

    if (!response.ok) {
      if (response.status === 401 && onUnauthorized) {
        onUnauthorized();
      }

      let errorData: ApiError | null = null;
      try {
        errorData = await response.json();
      } catch {
        // Response body is not JSON
      }

      // Retryability for this HTTP error: 5xx only (401 is never retried)
      const willRetry =
        retryCount < MAX_RETRIES &&
        response.status !== 401 &&
        response.status >= 500;
      const err = new ApiRequestError(
        errorData?.message || `HTTP error ${response.status}`,
        response.status,
        errorData?.code,
        errorData?.details,
        !willRetry
      );
      throw err;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const jsonData = await response.json();
    return jsonData;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      // Only report timeout when our timer fired; never report caller-initiated abort as timeout
      if (callerAborted) {
        throw err;
      }
      const cause = (err as Error & { cause?: unknown }).cause;
      if (cause === ABORT_REASON_TIMEOUT) {
        throw new ApiRequestError('Request timed out', 408);
      }
      throw err;
    }
    if (
      err instanceof ApiRequestError &&
      err.exhaustedRetries
    ) {
      throw err;
    }
    if (
      retryCount < MAX_RETRIES &&
      isRetryableError(err, lastStatus)
    ) {
      await delay(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount));
      return fetchApi<T>(endpoint, { ...options, signal: undefined }, retryCount + 1);
    }
    throw err;
  }
}

/**
 * HTTP GET request
 */
export function get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
  let url = endpoint;
  
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return fetchApi<T>(url, { method: 'GET' });
}

/**
 * HTTP POST request
 */
export function post<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * HTTP PUT request
 */
export function put<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
  return fetchApi<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * HTTP DELETE request
 */
export function del<T>(endpoint: string): Promise<T> {
  return fetchApi<T>(endpoint, { method: 'DELETE' });
}
